import { isBefore, parseISO } from 'date-fns';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isEmail from 'validator/lib/isEmail';
import isUUID from 'validator/lib/isUUID';
const translationOrString = (props, i18nKey, text, i18nData = null) => props && props.t && typeof props.t === 'function' ? props.t(`common:${i18nKey}`, i18nData) : text;
export const numbers = {
    required: (value, allValues, props) => !isNumber(value) ? translationOrString(props, `validators:numbers:required`, `Required`) : undefined,
    isHigherThan: (val) => (value, allValues, props) => value && Number(value) <= val
        ? translationOrString(props, `validators:numbers:isHigherThan`, `Value must be higher than ${val}`, { val })
        : undefined,
    isLowerThan: (val) => (value, allValues, props) => value && Number(value) >= val
        ? translationOrString(props, `validators:numbers:isLowerThan`, `Value must be lower than ${val}`, { val })
        : undefined,
    isHigherOrEqualThan: (val) => (value, allValues, props) => value && Number(value) < val
        ? translationOrString(props, `validators:numbers:isHigherOrEqualThan`, `Value must be higher or equal than ${val}`, { val })
        : undefined,
    isLowerOrEqualThan: (val) => (value, allValues, props) => value && Number(value) > val
        ? translationOrString(props, `validators:numbers:isLowerOrEqualThan`, `Value must be lower or equal than ${val}`, { val })
        : undefined,
};
export const strings = {
    notEmpty: (value, allValues, props) => !value || (value && String(value).trim().length === 0)
        ? translationOrString(props, `validators:strings:required`, `Required`)
        : undefined,
    hasExactlyChars: (charCount) => (value, allValues, props) => value && value.length !== charCount
        ? translationOrString(props, `validators:strings:hasExactlyChars`, `Value must be exactly ${charCount} chars longer`, { charCount })
        : undefined,
    isShorterThan: (charCount) => (value, allValues, props) => value && value.length >= charCount
        ? translationOrString(props, `validators:strings:isShorterThan`, `Value must be shorter than ${charCount} characters`, { charCount })
        : undefined,
    isShorterOrEqualThan: (charCount) => (value, allValues, props) => value && value.length > charCount
        ? translationOrString(props, `validators:strings:isShorterOrEqualThan`, `Value must be shorter or equal than ${charCount} characters`, { charCount })
        : undefined,
    isLongerThan: (charCount) => (value, allValues, props) => value && value.length <= charCount
        ? translationOrString(props, `validators:strings:isLongerThan`, `Value must be longer than ${charCount} characters`, { charCount })
        : undefined,
    isLongerOrEqualThan: (charCount) => (value, allValues, props) => value && value.length < charCount
        ? translationOrString(props, `validators:strings:isLongerOrEqualThan`, `Value must be longer or equal than ${charCount} characters`, { charCount })
        : undefined,
};
const cl10LongSerialRegExp = new RegExp(/(001|510)-[\d]{4}-[\d]{4}-[A-Za-z]{2}-[\d]{2}/);
const cl55LongSerialRegExp = new RegExp(/(002|511)-[\d]{4}-[\d]{4}-[A-Za-z]{2}-[\d]{2}/);
const cl10SerialRegExp = new RegExp(/(001|510)-[\d]{4}-[\d]{4}-[A-Za-z]{2}$/);
const cl55SerialRegExp = new RegExp(/(002|511)-[\d]{4}-[\d]{4}-[A-Za-z]{2}$/);
const serialNumberValidators = {
    isConsoleSerialNumber: (value, allValues, props) => {
        const snRegex = new RegExp(/(^[a-zA-Z0-9]{9}$)|(^[a-zA-Z0-9]{11}$)|(^[a-zA-Z0-9-]{17}$)/);
        return value && !snRegex.test(value)
            ? translationOrString(props, 'validators:format:console_serial_number', 'Invalid console S/N')
            : undefined;
    },
    isDeviceSerialNumber: (value, allValues, props) => {
        const snRegex = new RegExp(/^[a-zA-Z0-9-]{8,}$/);
        return (value && value.trim().length < 8) || !snRegex.test(value)
            ? translationOrString(props, 'validators:format:device_serial_number', 'Invalid device S/N')
            : undefined;
    },
    isCL10SerialNumber: (value, allValues, props) => {
        return value && !(cl10SerialRegExp.test(value) || cl10LongSerialRegExp.test(value))
            ? translationOrString(props, 'validators:format:cl10_serial_number', 'Invalid device S/N')
            : undefined;
    },
    isCL55SerialNumber: (value, allValues, props) => {
        return value && !(cl55SerialRegExp.test(value) || cl55LongSerialRegExp.test(value))
            ? translationOrString(props, 'validators:format:cl55_serial_number', 'Invalid device S/N')
            : undefined;
    },
};
export const dates = {
    endDateIsBeforeStart: (input, props) => (value, allValues) => {
        const { startDateFieldName, endDateFieldName } = input;
        if (allValues[startDateFieldName] && allValues[endDateFieldName]) {
            return isBefore(parseISO(allValues[endDateFieldName]), parseISO(allValues[startDateFieldName]))
                ? props.t('common:validators:dates:end_date_must_be_before_start_date')
                : undefined;
        }
        return !value || value.length <= 0 ? `Required` : undefined;
    },
};
export const booleans = {
    atLeastOneIsChecked: (t) => (value) => {
        return !value || value.length <= 0 ? t('common:validators:numbers:required') : undefined;
    },
};
export const format = {
    isPhoneNumber: (value, allValues, props) => {
        const phoneRegex = new RegExp(/(?:(\+\d{1,4}))?[\d ()\-.]{5,}$/);
        return value && !phoneRegex.test(value)
            ? translationOrString(props, 'validators:format:phone_number', 'Invalid phone number')
            : undefined;
    },
    isValidEmail: (value, allValues, props) => value && !isEmail(value)
        ? translationOrString(props, 'validators:format:email', 'Invalid email address')
        : undefined,
    isNumeric: (val) => isNil(val) ? false : /-?(\d+|\d+\.\d+|\.\d+)([eE][-+]?\d+)?/.test(val.toString()),
    isTextOnly: (value, allValues, props) => {
        const textOnlyRegex = new RegExp(/^([a-zA-Z']+\s)*[a-zA-Z']+$/);
        return value && !textOnlyRegex.test(value)
            ? translationOrString(props, 'validators:format:text_only', 'Only characters and spaces')
            : undefined;
    },
    isValidUUID: (value, allValues, props) => value && !isUUID(value) ? translationOrString(props, 'validators:format:uuid', 'Invalid UUID') : undefined,
    ...serialNumberValidators,
};
export const composeValidators = (validators, props) => (value, allValues) => validators.reduce((error, validator) => error || validator(value, allValues, props), undefined);
export const forms = {
    isChecked: (value, allValues, props) => !value
        ? translationOrString(props, 'validators:forms:checked', 'This checkbox must be checked to proceed')
        : undefined,
};
export default {
    numbers,
    strings,
    format,
    forms,
    dates,
    booleans,
    composeValidators,
};
//# sourceMappingURL=validations.js.map